import { DeleteOutlined, ExportOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Collapse, Descriptions, Table, message } from "antd";
import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Await,
  Form,
  redirect,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import {
  getProjectDetails,
  getPurchaseBillDetails,
  linkPurchaseBill,
  purchaseBillAuditTrail,
  removePurchaseBill,
} from "../../api/finance";
import { ResponsiveSideDrawer } from "../../components/drawer";
import PurcahseBillProjectItemSelect from "../../components/form/purchase-bill-project-item-select.jsx";
import Confirm from "../../components/message/Confirm.jsx";
import {
  ExecutiveEntitySelect,
  FieldWrapper,
  ProjectSelectAdvance,
  VendorSelect,
} from "../../components/inputs";
import { Page } from "../../components/page";
import { ValidationError } from "../../library/classes/exceptions/index.js";
import {
  InvoiceDetailsAuditTrailColumns,
  generateRouteUrl,
} from "../../library/constants/index.js";
import { Server } from "../../library/constants/server.js";
import { getTimeAgo } from "../../library/utilities/intl.js";
import { CancelButton } from "../../ui";
import Styles from "./purchasebills-basic-details.module.scss";
const { Panel } = Collapse;

const PurchaseBillLink = ({ title }) => {
  const navigate = useNavigate();
  const navigation = useNavigation();
  const isSubmitting =
    navigation.state === "submitting" || navigation.state === "loading";

  const {
    purchaseBillId,
    purchaseBill,
    email /* , projectDetails */,
    auditTrail,
  } = useLoaderData();
  const [pdfPreview, setPdfPreview] = useState(null);
  const [projectId, setProjectId] = useState(
    purchaseBill.project_id ?? purchaseBill.suggested_project?.id ?? null,
  );
  const [projectItemIds, setProjectItemIds] = useState(
    purchaseBill.item_ids ?? [],
  );
  // const [paymentMethodId, setPaymentMethodId] = useState(purchaseBill.payment_method_id)
  const [vendorId, setVendorId] = useState(
    purchaseBill.vendor_id ?? purchaseBill.suggested_vendor?.id ?? null,
  );
  const [entityId, setEntityId] = useState(purchaseBill.entity_id ?? null);
  const [isDeleting, setIsDeleting] = useState(false);
  // A purchase bill normally originates from an IMAP email, but it does not have to — one
  // created directly has no email_read, and `email` is then null. Reading through it crashed
  // the whole page rather than just omitting the email panel.
  const attachments = Array.isArray(email?.attachments)
    ? email.attachments.filter(Boolean)
    : [];
  const canCreatePurchaseBill =
    !!vendorId && (!!entityId || (!!projectId && projectItemIds.length > 0));

  // const submit = useSubmit();
  const { errors = null } = useActionData() || {};

  // const mainFormRef = useRef(null)

  const getAttatchmentUrl = (path) =>
    Server.getFileUrl(`document/attachment/${path}`);

  // const debounceUpdateHandler = useCallback(debounce((form) => {
  // 	fetcher.submit(form, { method: 'post' });
  // }, 1000), []);

  // const submitForm = () => debounceUpdateHandler(mainFormRef.current)
  return (
    <>
      <Page className="purchase-bill-create" title={title} backLink={generateRouteUrl("PurchaseBills")}>
        <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
          <Panel
            header={
              <>
                <MailOutlined /> Emails
              </>
            }
            key="1"
            extra={
              <Confirm
                danger
                type="primary"
                icon={<DeleteOutlined />}
                loading={isDeleting}
                disabled={isDeleting}
                title="Delete Purchase Bill"
                content="Are you sure you want to delete this purchase bill? This action cannot be undone."
                okText="Yes, Delete"
                cancelText="Cancel"
                onClick={(e) => e.stopPropagation()}
                onConfirm={async () => {
                  try {
                    setIsDeleting(true);
                    const msg = await removePurchaseBill(purchaseBillId);
                    message.success(msg || "Purchase bill deleted successfully.");
                    navigate(generateRouteUrl("PurchaseBills"));
                  } catch (error) {
                    setIsDeleting(false);
                    message.error(error.message);
                  }
                }}
              >
                Delete Purchase Bill
              </Confirm>
            }
          >
            <Descriptions
              className="email-descriptions descriptions--responsive-compact"
              column={pdfPreview ? 1 : { xs: 1, sm: 1, md: 2, lg: 3 }}
            >
              <Descriptions.Item label="From">{email?.from ?? "-"}</Descriptions.Item>
              <Descriptions.Item label="Subject">
                {email?.subject ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Files">
                {attachments.length > 0 ? (
                  <div className="attachment-list">
                    {attachments.map((attachment, index) => (
                      <Button
                        className="attachment-list__button"
                        key={`${attachment}-${index}`}
                        title={attachment}
                        type="link"
                        onClick={() => setPdfPreview(attachment)}
                      >
                        <span className="attachment-list__name">
                          {attachment}
                        </span>
                      </Button>
                    ))}
                  </div>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Recieved At">
                {email?.createdAt ? getTimeAgo(email.createdAt) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Content">
                {email?.body
                  ?.split("\n")
                  .slice(0, 5)
                  .map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br></br>
                    </React.Fragment>
                  ))}
              </Descriptions.Item>
            </Descriptions>
          </Panel>
        </Collapse>
        <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
          <Panel
            header={
              <>
                <MailOutlined /> Project Search
              </>
            }
            key="1"
          >
            <Form
              className={Styles["fieldsWrapper--Two"]}
              method="post"
              style={{ gridTemplateColumns: "1fr auto 1fr" }}
            >
              <FieldWrapper
                name="vendor_id"
                label="Vendor"
                errors={errors}
                style={{
                  gridColumn: "1 / -1",
                  justifySelf: "center",
                  width: "min(100%, 32rem)",
                }}
              >
                <VendorSelect
                  value={vendorId}
                  currentSelection={{
                    value: purchaseBill.vendor_id ?? purchaseBill.suggested_vendor?.id,
                    label:
                      purchaseBill.vendor?.company ??
                      purchaseBill.suggested_vendor?.company,
                  }}
                  onChange={(v) => {
                    // Only clear a real prior pick — clearing on the first vendor
                    // confirmation would wipe the suggested project/items.
                    if (vendorId) {
                      setProjectId(null);
                      setProjectItemIds([]);
                      setEntityId(null);
                    }
                    setVendorId(v);
                  }}
                />
                <input type="hidden" name="vendor_id" value={vendorId ?? ""} />
              </FieldWrapper>
              <FieldWrapper name="item_ids" label="Project" errors={errors}>
                <ProjectSelectAdvance
                  key={projectId}
                  filters={{
                    vendor_id: vendorId,
                  }}
                  value={projectId}
                  currentSelection={{
                    value: purchaseBill.suggested_project?.id,
                    label: purchaseBill.suggested_project
                      ? `#${purchaseBill.suggested_project.id} : ${purchaseBill.suggested_project.company}`
                      : undefined,
                  }}
                  onChange={(v) => {
                    setProjectId(v);
                    setProjectItemIds([]);
                    setEntityId(null);
                  }}
                  disabled={!vendorId}
                />
                <input type="hidden" name="project_id" value={projectId ?? ""} />
              </FieldWrapper>
              <div
                aria-hidden="true"
                style={{
                  alignSelf: "center",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  paddingTop: "1.5rem",
                }}
              >
                OR
              </div>
              <FieldWrapper name="entity_id" label="Entity" errors={errors}>
                <ExecutiveEntitySelect
                  value={entityId}
                  onChange={(v) => {
                    setEntityId(v);
                    setProjectId(null);
                    setProjectItemIds([]);
                  }}
                  disabled={!vendorId}
                  allowClear
                />
                <input type="hidden" name="entity_id" value={entityId ?? ""} />
              </FieldWrapper>
              {!!projectId && (
                <FieldWrapper
                  name="item_ids"
                  label="Items"
                  errors={errors}
                  style={{ gridColumn: "1 / -1" }}
                >
                  <PurcahseBillProjectItemSelect
                    key={`${vendorId}-${projectId}`}
                    filters={{
                      vendor_id: vendorId,
                      project_id: projectId,
                    }}
                    value={projectItemIds}
                    onChange={(v, o) => {
                      if (o?.[0]?.project_id) {
                        setProjectId(o?.[0]?.project_id ?? null);
                      }
                      setProjectItemIds(v);
                    }}
                    disabled={!projectId}
                    mode="multiple"
                  />
                  <input
                    type="hidden"
                    name="item_id"
                    value={projectItemIds ?? ""}
                  />
                </FieldWrapper>
              )}
              <div
                className="actions align--center"
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  htmlType="submit"
                  type="primary"
                  loading={isSubmitting}
                  disabled={!canCreatePurchaseBill || isDeleting || isSubmitting}
                >
                  Create Purchase Bill
                </Button>
                <CancelButton
                  disabled={isDeleting || isSubmitting}
                  onClick={() => navigate(generateRouteUrl("PurchaseBills"))}
                >
                  Cancel
                </CancelButton>
              </div>
            </Form>
          </Panel>
        </Collapse>
        <React.Suspense>
          <Await
            resolve={auditTrail}
            errorElement={<p>Error loading audit trail!</p>}
          >
            {(logs) => (
              <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
                <Panel
                  header={
                    <>
                      <img
                        src="/icons/headericons/audittrail.svg"
                        style={{
                          width: "1.25rem",
                          verticalAlign: "sub",
                          marginRight: "8px",
                        }}
                      />
                      Audit Trail
                    </>
                  }
                  key="1"
                >
                  <Table
                    columns={InvoiceDetailsAuditTrailColumns}
                    dataSource={logs}
                    rowKey="id"
                    pagination={{ hideOnSinglePage: true }}
                  />
                </Panel>
              </Collapse>
            )}
          </Await>
        </React.Suspense>
      </Page>
      <ResponsiveSideDrawer
        title={
          <>
            <Button
              type="primary"
              href={getAttatchmentUrl(pdfPreview)}
              icon={<ExportOutlined />}
              target="_blank"
            >
              View file
            </Button>{" "}
          </>
        }
        placement="right"
        onClose={() => setPdfPreview(null)}
        open={!!pdfPreview && pdfPreview.length > 0}
        desktopWidth={ResponsiveSideDrawer.DRAWER_WIDTH_WIDE}
        size="large"
        mask={false}
        getContainer={false}
        destroyOnClose
      >
        <object
          data={getAttatchmentUrl(pdfPreview)}
          type="application/pdf"
          width="100%"
          height="100%"
        >
          <p>Unable to load file.Click on the link below to view the file</p>
          <p>
            <a
              target="_blank"
              rel="noreferrer"
              href={getAttatchmentUrl(pdfPreview)}
            >
              {pdfPreview}
            </a>
          </p>
        </object>
      </ResponsiveSideDrawer>
    </>
  );
};

PurchaseBillLink.propTypes = {
  title: PropTypes.string,
};

PurchaseBillLink.Action = async ({ params, request }) => {
  const { purchaseBillId } = params;
  const { ...data } = Object.fromEntries(await request.formData());
  try {
    const { vendor_id, item_id = "", entity_id, project_id } = data;
    const itemIds = item_id
      ? item_id.split(",").map(Number).filter(Boolean)
      : [];
    const msg = await linkPurchaseBill(
      purchaseBillId,
      Number(vendor_id),
      itemIds.length ? itemIds : null,
      entity_id ? Number(entity_id) : null,
      project_id ? Number(project_id) : null,
    );
    message.success(msg);
    return redirect(
      generateRouteUrl("FinancePurchaseBillDetails", {
        purchaseBillId,
      }),
    );
  } catch (error) {
    message.error(error.message);
    if (error instanceof ValidationError) {
      return {
        errors: error.errors,
      };
    }
  }
  return true;
};

PurchaseBillLink.Loader = async ({ params }) => {
  if (isNaN(params.purchaseBillId)) {
    throw new Error("Invalid Purchase Bill ID");
  }

  const {
    id: purchaseBillId,
    email_read: email,
    purchase_bill_items,
    status,
    ...purchaseBill
  } = await getPurchaseBillDetails(params.purchaseBillId);

  if (purchaseBill.purchase_bill_linked) {
    return redirect(generateRouteUrl("PurchaseBills"));
  }

  purchaseBill.item_ids = purchase_bill_items.map(({ item_id }) => item_id);

  let projectDetails = {};
  if (purchaseBill.projectId) {
    projectDetails = await getProjectDetails(purchaseBill.projectId);
  }

  const auditTrail = purchaseBillAuditTrail(params.purchaseBillId);

  return {
    purchaseBillId,
    email,
    status,
    purchaseBill,
    projectDetails,
    auditTrail,
  };
};

export default PurchaseBillLink;
